import qs from "qs";
import { generatePath } from "react-router";

import { SmartGroupChartPoint } from "shared/api/schemaAnalytics/types";
import { SchemaDashboardAttributeGrouping } from "shared/api/schemaDefinitions/types";
import { MONTH_DAY_NUMERIC } from "shared/constants";
import { formatDate } from "shared/locale/format";

import {
  SCHEMA_INVESTIGATOR_ASSET_FILTER_KEY,
  SCHEMA_INVESTIGATOR_EVENT_FILTER_KEY,
  SCHEMA_INVESTIGATOR_SMART_GROUP_ID_KEY,
} from "pages/SchemaInvestigator/constants";
import { createAttributeGroupEventFilter } from "pages/shared/Dashboard/attribute-group-utils";
import { SchemaSmartGroupCountType } from "pages/shared/schemaDefinitions/constants";

import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
  simplifyFilterGroupState,
} from "features/ui/Filters/FilterBuilder/utils";

import { routes } from "services/routes";

export const SMART_GROUP_CHART_Y_AXIS_KEY = "value";
const STACKED_BAR_KEY = "comparisonDifference";

export const formatChartDateTick = (value: string): string =>
  formatDate(value, MONTH_DAY_NUMERIC);

// When the comparison overlay is hidden, plot the filtered series directly.
// Otherwise build stacked data so each bucket has a bottom bar (filter result)
// and a top bar (the comparison-filter result minus the filter result, never
// negative). Dates are driven by the comparison series so the x-axis spans the
// full comparison window even when the filtered series is sparser.
export const buildSmartGroupChartData = (
  chart: SmartGroupChartPoint[],
  comparisonChart: SmartGroupChartPoint[],
  hideComparison: boolean
) => {
  if (hideComparison) {
    return {
      data: chart.map((point) => ({
        date: point.date,
        [SMART_GROUP_CHART_Y_AXIS_KEY]: point.value,
      })),
      stackedBarKey: undefined,
    };
  }

  const filteredByDate = new Map(chart.map((p) => [p.date, p.value]));

  const data = comparisonChart.map((point) => {
    const filteredValue = filteredByDate.get(point.date) ?? 0;
    const difference = Math.max(0, point.value - filteredValue);

    return {
      date: point.date,
      [SMART_GROUP_CHART_Y_AXIS_KEY]: filteredValue,
      [STACKED_BAR_KEY]: difference,
    };
  });

  return { data, stackedBarKey: STACKED_BAR_KEY };
};

// Maps the "Prioritize by" toggle to the server-side ranking sort
// (`<metric>:<direction>`), so the first page is ranked by the metric the user
// is viewing instead of the backend's default event-count order.
export const countTypeToSort = (
  countType: SchemaSmartGroupCountType
): string =>
  countType === SchemaSmartGroupCountType.ASSET_COUNT
    ? "assetCount:desc"
    : "eventCount:desc";

interface SchemaInvestigatorTargetArgs {
  /** Absent for attribute groups, whose constraint is carried by eventFilter instead. */
  groupID?: string;
  /** Merged dashboard event filter query string ("" when none). */
  eventFilter: string;
  /** Asset filter query string ("" when none). */
  assetFilter: string;
  tableType: string;
  schemaName: string;
}

// The event filter the investigator opens with: the dashboard's own filters, plus the group's
// constraint when grouping by an attribute. An attribute group has no id to hand over, so its
// constraint travels as an ordinary filter row the investigator can then edit.
export const buildInvestigatorEventFilter = ({
  eventFilter,
  tableFilters,
  attributeGrouping,
  groupID,
}: {
  eventFilter: FilterGroupState | undefined;
  tableFilters: FilterGroupState | undefined;
  attributeGrouping: SchemaDashboardAttributeGrouping | undefined;
  groupID: string;
}): string =>
  getFiltersQuery(
    simplifyFilterGroupState(
      mergeFilterGroupStates(
        eventFilter,
        tableFilters,
        attributeGrouping &&
          createAttributeGroupEventFilter(attributeGrouping.attribute, groupID)
      )
    )
  );

// Builds the SchemaInvestigator navigation target (pathname + search) for a
// single smart group, carrying the dashboard's merged event filter and asset
// filter plus the group id. Empty filters are dropped from the query string so
// the investigator isn't handed blank `eventFilter=` / `assetFilter=` params.
export const buildSchemaInvestigatorTarget = ({
  groupID,
  eventFilter,
  assetFilter,
  tableType,
  schemaName,
}: SchemaInvestigatorTargetArgs): { pathname: string; search: string } => ({
  pathname: generatePath(routes.schemaInvestigator, {
    tableType,
    schemaName,
  }),
  search: qs.stringify({
    [SCHEMA_INVESTIGATOR_EVENT_FILTER_KEY]: eventFilter || undefined,
    [SCHEMA_INVESTIGATOR_ASSET_FILTER_KEY]: assetFilter || undefined,
    [SCHEMA_INVESTIGATOR_SMART_GROUP_ID_KEY]: groupID,
  }),
});
